import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Sparkles,
  Loader2,
  Check,
  X,
  Pencil,
  AlertTriangle,
  RotateCcw,
  FileOutput,
} from 'lucide-react';
import {
  analyzeWalkthrough,
  fetchWalkthroughStatus,
  listFindings,
  reviewFinding,
  draftScopeFromFindings,
} from '@/lib/pqvData';
import { toast } from 'sonner';

/* ---------------------------------------------------------------------------
 * FindingsReview — PQ Vision AI findings, grouped by area, with the
 * contractor's confirm / edit / reject loop.
 *
 * Design law: AI proposes, the contractor decides. Nothing here is trusted
 * until confirmed. Quantities are shown as FLAGGED proposals ("confirm?"),
 * never as facts. Every decision is a labeled learning signal (handled in
 * reviewFinding).
 * ------------------------------------------------------------------------- */

const TYPE_STYLE = {
  scope: 'bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30',
  condition: 'bg-blue-500/12 text-blue-700 dark:text-blue-300 border-blue-400/30',
  material: 'bg-slate-500/12 text-slate-700 dark:text-slate-300 border-slate-400/30',
  complication: 'bg-amber-500/12 text-amber-700 dark:text-amber-300 border-amber-400/30',
  missed: 'bg-red-500/12 text-red-700 dark:text-red-300 border-red-400/30',
  verify: 'bg-violet-500/12 text-violet-700 dark:text-violet-300 border-violet-400/30',
};

const confidencePct = (c) =>
  c === null || c === undefined ? null : Math.round(Number(c) * 100);

const QuantityFlag = ({ quantity }) => {
  if (!quantity || typeof quantity !== 'object') return null;
  const { value, unit, basis } = quantity;
  if (value === undefined && !unit) return null;
  return (
    <div className="mt-2 inline-flex items-start gap-1.5 rounded-md border border-amber-400/40 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-800 dark:text-amber-200">
      <AlertTriangle size={12} className="mt-[1px] shrink-0" />
      <span>
        <span className="font-semibold">
          ~{value}
          {unit ? ` ${unit}` : ''}
        </span>{' '}
        — confirm{basis ? ` (${basis})` : ''}
      </span>
    </div>
  );
};

const FindingCard = ({ finding, onReview }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(finding.description || '');
  const [busy, setBusy] = useState(false);
  const pct = confidencePct(finding.confidence);
  const status = finding.status;
  const decided = status === 'confirmed' || status === 'rejected';

  const act = async (next, edits) => {
    setBusy(true);
    try {
      await onReview(finding, next, edits);
    } finally {
      setBusy(false);
      setEditing(false);
    }
  };

  return (
    <div
      className={`rounded-md border p-3 ${
        status === 'rejected'
          ? 'border-border bg-[hsl(var(--muted))]/30 opacity-60'
          : status === 'confirmed'
          ? 'border-emerald-500/30 bg-emerald-500/[0.05]'
          : 'border-border bg-[hsl(var(--card))]'
      }`}
      data-testid={`finding-${finding.id}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`text-[10px] font-semibold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border ${
            TYPE_STYLE[finding.type] || TYPE_STYLE.scope
          }`}
        >
          {finding.type}
        </span>
        {pct !== null ? (
          <span className="text-[10px] text-muted-foreground tabular-nums">
            {pct}% confidence
          </span>
        ) : null}
        {status === 'confirmed' ? (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-300">
            <Check size={12} /> Confirmed
          </span>
        ) : status === 'rejected' ? (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Rejected
          </span>
        ) : null}
      </div>

      {editing ? (
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="mt-2 min-h-[64px] bg-[hsl(var(--muted))] border-border text-foreground text-sm"
          autoFocus
        />
      ) : (
        <p className="mt-2 text-sm text-foreground leading-relaxed">
          {finding.description}
        </p>
      )}

      {/* Answered in chat (PQ's resolve_finding) — settled, PQ won't raise it again. */}
      {finding.resolution && !editing ? (
        <div
          className="mt-2 rounded border border-[hsl(150_56%_50%)]/35 bg-[hsl(150_56%_50%)]/10 px-2 py-1.5 text-[12px] text-foreground"
          data-testid={`finding-resolution-${finding.id}`}
        >
          <span className="font-semibold text-[hsl(150_56%_45%)] uppercase tracking-[0.12em] text-[10px] mr-1.5">
            Answered{finding.resolved_at ? ` ${String(finding.resolved_at).slice(0, 10)}` : ''}
          </span>
          {finding.resolution}
        </div>
      ) : null}

      {finding.evidence && !editing ? (
        <p className="mt-1 text-[12px] italic text-muted-foreground leading-relaxed">
          {finding.evidence}
        </p>
      ) : null}

      {!editing ? <QuantityFlag quantity={finding.quantity} /> : null}

      {/* Actions */}
      <div className="mt-3 flex items-center gap-1.5">
        {editing ? (
          <>
            <Button
              type="button"
              size="sm"
              onClick={() => act('edited', { description: draft.trim() })}
              disabled={busy || !draft.trim()}
              className="h-7 bg-primary text-primary-foreground hover:bg-primary/90 text-xs"
            >
              Save
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditing(false);
                setDraft(finding.description || '');
              }}
              className="h-7 text-muted-foreground text-xs"
            >
              Cancel
            </Button>
          </>
        ) : decided ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => act('proposed')}
            disabled={busy}
            className="h-7 text-muted-foreground hover:text-foreground text-xs"
          >
            <RotateCcw size={13} className="mr-1" /> Undo
          </Button>
        ) : (
          <>
            <Button
              type="button"
              size="sm"
              onClick={() => act('confirmed')}
              disabled={busy}
              className="h-7 bg-emerald-600 text-white hover:bg-emerald-600/90 text-xs"
              data-testid={`finding-confirm-${finding.id}`}
            >
              {busy ? (
                <Loader2 size={13} className="mr-1 animate-spin" />
              ) : (
                <Check size={13} className="mr-1" />
              )}
              Confirm
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setEditing(true)}
              disabled={busy}
              className="h-7 border-border text-foreground hover:bg-[hsl(var(--muted))] text-xs"
            >
              <Pencil size={13} className="mr-1" /> Edit
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => act('rejected')}
              disabled={busy}
              className="h-7 text-muted-foreground hover:text-[hsl(var(--destructive))] text-xs"
            >
              <X size={13} className="mr-1" /> Reject
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export const FindingsReview = ({ walkthroughId, estimateId = null, areas = [] }) => {
  const navigate = useNavigate();
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [drafting, setDrafting] = useState(false);

  // The closing arrow of the Vision loop: confirmed findings → estimate scope.
  const runDraftScope = async () => {
    setDrafting(true);
    try {
      const res = await draftScopeFromFindings(walkthroughId);
      if (res.added > 0) {
        toast.success(
          `Drafted ${res.added} section${res.added === 1 ? '' : 's'} into "${
            res.estimateTitle
          }" from ${res.confirmedUsed} confirmed finding${
            res.confirmedUsed === 1 ? '' : 's'
          }. Open it to review & price.`
        );
        // Land inside the estimate the scope was just drafted into.
        navigate(
          estimateId
            ? `/dashboard/estimates?id=${estimateId}`
            : '/dashboard/estimates'
        );
      } else {
        toast.info(
          res.skippedExisting > 0
            ? 'Nothing new to draft — those areas already have sections on the estimate.'
            : 'Nothing to draft yet.'
        );
      }
    } catch (e) {
      toast.error(e?.message || 'Could not draft the scope.');
    } finally {
      setDrafting(false);
    }
  };

  const areaName = useCallback(
    (id) => areas.find((a) => a.id === id)?.name || 'Area',
    [areas]
  );

  const load = useCallback(async () => {
    if (!walkthroughId) return;
    setLoading(true);
    try {
      setFindings(await listFindings(walkthroughId));
    } catch (e) {
      toast.error(e?.message || 'Could not load findings.');
    } finally {
      setLoading(false);
    }
  }, [walkthroughId]);

  useEffect(() => {
    load();
  }, [load]);

  // Background analysis (pqv-analyze answers { pending } immediately and works
  // detached — big walkthroughs used to blow the request wall clock and the
  // contractor saw a bare non-2xx). Poll the walkthrough status until it
  // leaves 'analyzing', then reload findings. Resumes on reopen too.
  const pollRef = useRef(null);
  const stopAnalysisPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);
  const startAnalysisPoll = useCallback(() => {
    stopAnalysisPoll();
    setAnalyzing(true);
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      try {
        const status = await fetchWalkthroughStatus(walkthroughId);
        if (status !== 'analyzing') {
          stopAnalysisPoll();
          setAnalyzing(false);
          await load();
          toast.success('Analysis finished — review the findings below.');
          return;
        }
      } catch (_) {
        /* transient read failure — keep polling */
      }
      if (ticks > 225) {
        // ~15 minutes — the stale-lock guard server-side will free it.
        stopAnalysisPoll();
        setAnalyzing(false);
        toast.error('Analysis is taking unusually long. Reopen this walkthrough in a few minutes.');
      }
    }, 4000);
  }, [walkthroughId, load, stopAnalysisPoll]);
  useEffect(() => stopAnalysisPoll, [stopAnalysisPoll]);

  // Reopened mid-analysis? Pick the spinner + poll back up.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await fetchWalkthroughStatus(walkthroughId);
        if (!cancelled && status === 'analyzing') startAnalysisPoll();
      } catch (_) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [walkthroughId, startAnalysisPoll]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await analyzeWalkthrough(walkthroughId);
      if (res?.pending) {
        toast.success('PQ is analyzing — you can keep working; findings will appear here.');
        startAnalysisPoll();
        return;
      }
      await load();
    } catch (e) {
      setAnalyzing(false);
      toast.error(e?.message || 'Analysis failed. Check the API key / function.');
    }
  };

  const onReview = async (finding, status, edits) => {
    // Optimistic: patch local state, then persist.
    setFindings((prev) =>
      prev.map((f) =>
        f.id === finding.id
          ? { ...f, status, ...(edits?.description ? { description: edits.description } : {}) }
          : f
      )
    );
    try {
      await reviewFinding({ ...finding, area_name: areaName(finding.area_id) }, status, edits);
    } catch (e) {
      toast.error(e?.message || 'Could not save your decision.');
      await load(); // resync on failure
    }
  };

  const grouped = useMemo(() => {
    const byArea = new Map();
    for (const f of findings) {
      const arr = byArea.get(f.area_id) || [];
      arr.push(f);
      byArea.set(f.area_id, arr);
    }
    return Array.from(byArea.entries());
  }, [findings]);

  // Edited findings are contractor-approved too — they draft alongside confirmed.
  const confirmedCount = findings.filter(
    (f) => f.status === 'confirmed' || f.status === 'edited'
  ).length;

  return (
    <div className="space-y-3" data-testid="pqv-findings-review">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[hsl(var(--primary))]" />
          <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-foreground">
            PQ Vision Analysis
          </h3>
          {findings.length > 0 ? (
            <span className="text-[11px] text-muted-foreground">
              {confirmedCount}/{findings.length} confirmed
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {confirmedCount > 0 ? (
            <Button
              type="button"
              size="sm"
              onClick={runDraftScope}
              disabled={drafting || analyzing}
              className="bg-emerald-600 text-white hover:bg-emerald-600/90 uppercase tracking-[0.16em] text-xs"
              data-testid="pqv-draft-scope-button"
            >
              {drafting ? (
                <Loader2 size={14} className="mr-2 animate-spin" />
              ) : (
                <FileOutput size={14} className="mr-2" />
              )}
              Draft scope ({confirmedCount})
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            onClick={runAnalysis}
            disabled={analyzing || drafting}
            className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
            data-testid="pqv-analyze-button"
          >
            {analyzing ? (
              <Loader2 size={14} className="mr-2 animate-spin" />
            ) : (
              <Sparkles size={14} className="mr-2" />
            )}
            {findings.length > 0 ? 'Re-analyze' : 'Analyze photos'}
          </Button>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground leading-relaxed">
        AI proposes — you decide. Confirm what&apos;s right, edit what&apos;s
        close, reject what&apos;s off. Quantities are always flagged to confirm,
        never trusted. Every decision trains PQ Intelligence on how you bid.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <Loader2 size={14} className="animate-spin" /> Loading findings…
        </div>
      ) : findings.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-[hsl(var(--muted))]/30 px-4 py-6 text-center text-sm text-muted-foreground">
          No findings yet. Add photos to your areas, then run{' '}
          <span className="font-medium text-foreground">Analyze photos</span>.
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([areaId, list]) => (
            <div key={areaId}>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-1.5">
                {areaName(areaId)}
              </div>
              <div className="space-y-2">
                {list.map((f) => (
                  <FindingCard key={f.id} finding={f} onReview={onReview} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FindingsReview;
